import { getFrameInfos, type FrameInfo } from '@microsoft/wv2-outlook/instanceIframeTracker';
import type { IframeScenario } from '../settings/iframes-mapping';
import { getIframeSettings } from '../hooks/useIframeSettings';
import { trace } from 'owa-trace';
import { setIframeInfo } from './getIframeInfo';

const iframesPerProcessCount = new Map<number, Set<string>>();

// IMPORTANT: This currently tracks the total memory for all iframes matching the scenario
// We will eventually change it to track per-iframe using query parameters with unique IDs.
export async function getIframeMemory(
    scenario: IframeScenario,
    iframeId: string
): Promise<number | undefined> {
    // Outside of Monarch, doesn't try to read the memory info
    if (!getFrameInfos) {
        return undefined;
    }

    try {
        const iframes = await getFrameInfos?.();

        trace.info(
            ` Getting iframe memory for scenario: ${scenario}. Iframes: ${JSON.stringify(iframes)}`,
            'iframeWrapper'
        );

        return getMemoryFromFrameInfos(scenario, iframeId, iframes);
    } catch {
        // no-op this will fail on the web
        return undefined;
    }
}

export function getMemoryFromFrameInfos(
    scenario: IframeScenario,
    iframeId: string,
    iframes: FrameInfo[] | undefined
): number | undefined {
    const matchedIframes = getIframesForScenario(scenario, iframes);

    if (matchedIframes?.length === 0 || !matchedIframes) {
        return undefined;
    }

    // Track number of iframes per process
    const processId = matchedIframes[0].processId;
    const iframesInProcess = iframesPerProcessCount.get(processId) ?? new Set<string>();

    if (!iframesInProcess.has(iframeId)) {
        iframesInProcess.add(iframeId);
        iframesPerProcessCount.set(processId, iframesInProcess);

        setIframeInfo(scenario, 'nativeHostProcessId', processId);
        setIframeInfo(scenario, 'iframesCountPerProcess', iframesInProcess.size);

        trace.info(
            ` Added iframe ${iframeId} in nativeHostProcessId ${processId}. Total iframes in process: ${
                iframesInProcess.size
            } (${Array.from(iframesInProcess).join(', ')})`,
            'iframeWrapper'
        );
    }

    const totalMemory = matchedIframes?.reduce(
        (sum, iframe) => sum + (iframe.workingSetSize ?? 0),
        0
    );

    return totalMemory;
}

export function removeIframeFromProcessCount(iframeId: string): void {
    for (const [processId, iframesSet] of iframesPerProcessCount.entries()) {
        if (iframesSet.has(iframeId)) {
            iframesSet.delete(iframeId);
            if (iframesSet.size === 0) {
                iframesPerProcessCount.delete(processId);
            } else {
                iframesPerProcessCount.set(processId, iframesSet);
            }

            trace.info(
                ` Removed iframe ${iframeId} from nativeHostProcessId ${processId}. Remaining iframes in process: ${
                    iframesSet ? iframesSet.size : 0
                } (${iframesSet ? Array.from(iframesSet).join(', ') : 'none'})`,
                'iframeWrapper'
            );
        }
    }
}

function getIframesForScenario(scenario: IframeScenario, iframes: FrameInfo[] | undefined) {
    const { matchUrl } = getIframeSettings(scenario);
    const matchedIframes = iframes?.filter(iframe => matchUrl(iframe.iframeSource));

    return matchedIframes;
}
